import { inject, Injectable } from '@angular/core';
import moment from 'moment';
import { catchError, EMPTY, firstValueFrom, forkJoin, from } from 'rxjs';
import { first, map, switchMap, take } from 'rxjs/operators';
import { BxLogger } from '../../core/services/bx-logger.service';
import { DialogService } from '../../core/services/dialog.service';
import { AsideModalSize, HelperService } from '../../core/services/helper.service';
import { StoresService } from '../../core/services/stores';
import { Customer } from '../../data/models';
import { RepoClientsService } from '../../data/repositories/repo-clients.service';
import { BreezeCase } from '../../utils/types/convert-case';
import { RemindersService } from '../dashboard/reminders.service';
import { MessagesService } from '../messages/messages.service';
import { AddClientComponent } from './add-client/add-client.component';
import { ClientDetailsViewComponent } from './client-details/client-details-view/client-details-view.component';
import { ImportClientsComponent } from './import-clients/import-clients.component';
import { Router } from '@angular/router';
import { ClientsApiService } from './domain/apis/clients-api.service';
import { Uuid } from '../../../graphql';

@Injectable({
    providedIn: 'root',
})
export class ClientsService {
    #clientsApiService = inject(ClientsApiService);

    constructor(
        private helperService: HelperService,
        private router: Router,
        private messagesService: MessagesService,
        private storesService: StoresService,
        private logger: BxLogger,
        private remindersService: RemindersService,
        private repoCustomers: RepoClientsService,
        private dialogService: DialogService
    ) {}

    // @ts-ignore TS7006
    newCustomer(options) {
        return this.showModalDialog(options);
    }

    // @ts-ignore TS7006
    showModalDialog(options) {
        const promise = new Promise<BreezeCase<Customer>>((resolve, reject) => {
            let initialState;
            if (options) {
                initialState = { ...options };
            }
            const modalRef = this.helperService.showModalAside(AddClientComponent, { initialState });
            // @ts-ignore TS2532
            modalRef.content.action.subscribe((action) => {
                if (action.cancelled) {
                    reject();
                } else {
                    if (!action.result.options.preventOpen) {
                        this.router.navigate(['/customers', 'list', action.result.customer.ID_Customer]);
                    }
                    this.storesService.refreshCustomers();
                    resolve(action.result.customer);
                }
            });
        });
        return promise;
    }

    importExcel() {
        const promise = new Promise<void>((resolve, reject) => {
            // @ts-ignore TS2345
            const modalRef = this.helperService.showModalAside(ImportClientsComponent, null, AsideModalSize.medium);

            // @ts-ignore TS2532
            modalRef.content.action.subscribe((action) => {
                if (action.cancelled) {
                    reject();
                } else {
                    this.storesService.refreshCustomers();
                    resolve();
                }
            });
        });
        return promise;
    }

    async importEstimatesJobs() {
        const result = await this.repoCustomers.Migrate();
        this.logger.success('Import completed - ' + result.data.toString() + ' new customers created.');
        this.storesService.refreshCustomers();
    }

    async emailCustomer(customerId: string, emailConfig = {}) {
        const contacts = await this._getContacts(customerId);
        let cfg: any = {
            To: contacts.email,
            contact: contacts.email,
            ReferenceId: customerId,
            references: [
                {
                    Type: 'customer',
                    Id: customerId,
                },
            ],
        };

        cfg = { ...emailConfig, ...cfg };
        return await this.messagesService.sendEmail(cfg);
    }

    async smsCustomer(customerId: string) {
        const contacts = await this._getContacts(customerId);
        await firstValueFrom(
            this.messagesService.sendSMS({
                to: contacts.sms,
                contacts: contacts.sms,
                referenceId: customerId,
            })
        );
    }

    async _getContacts(customerId: string) {
        const c = await this.repoCustomers.Get(customerId);
        return this.repoCustomers.GetEmailSMSContacts(c);
    }

    async newReminder(customerId: string) {
        const contacts = await this._getContacts(customerId);
        const initData: any = {
            ID_Reference: customerId,
            SMSRecipient: contacts.sms
                .map((s) => {
                    // @ts-ignore TS2339
                    return s.mobile;
                })
                .join(';'),
            EmailRecipient: contacts.email
                .map((e) => {
                    // @ts-ignore TS2339
                    return e.email;
                })
                .join(';'),
            ModuleReference: 'customers',
        };
        initData.SendSMS = !!initData.SMSRecipient;
        initData.SendEmail = !!initData.EmailRecipient;
        const reminderConfig = {
            contacts: contacts.all,
        };
        return this.remindersService.newReminder(initData, reminderConfig);
    }

    async deleteCustomer(customerId: Uuid) {
        const counts = await firstValueFrom(this.getLeadsEstimatesAndJobsCount(customerId));
        let countString = '';

        if (counts.leads) countString += counts.leads.toString() + ' lead' + (counts.leads > 1 ? 's ' : '');

        if (counts.estimates) {
            if (countString) countString += ' and ';

            countString += counts.estimates.toString() + ' estimate' + (counts.estimates > 1 ? 's' : '');
        }

        if (counts.jobs) {
            if (countString) countString += ' and ';
            countString += counts.jobs.toString() + ' job' + (counts.jobs > 1 ? 's' : '');
        }

        if (countString) countString = 'This client has ' + countString + ' linked to them. ';

        const params = {
            title: 'Delete customer?',
            text: `${countString} You can recover this client by selecting 'Settings', then 'Deleted Items' in the client list.`,
        };
        try {
            await this.dialogService.show(params);
            await firstValueFrom(this.#clientsApiService.deleteClient(customerId));
            this.logger.success('Client has been deleted');
            this.storesService.refreshCustomers();
            return true;
        } catch (err: any) {
            if (!!err) {
                this.logger.error('An error occurred trying to delete the client.');
            }
            // otherwise cancelled
            return false;
        }
    }

    getLeadsEstimatesAndJobsCount(customerId: Uuid) {
        const leadsCount$ = this.#clientsApiService.getLeadsCount(customerId).pipe(
            first(),
            map((result) => result.data.leadsCountByCustomerId)
        );
        const jobsCount$ = this.#clientsApiService.getJobsCount(customerId).pipe(
            first(),
            map((result) => result.data.jobs?.totalCount ?? 0)
        );
        const estimatesCount$ = this.#clientsApiService.getEstimatesCount(customerId).pipe(
            first(),
            map((result) => result.data.estimates?.totalCount ?? 0)
        );
        return forkJoin({ leads: leadsCount$, jobs: jobsCount$, estimates: estimatesCount$ });
    }

    async recoverCustomer(customerId: string, name: string) {
        const params = {
            title: 'Recover Client?',
            text: "The client '" + name + "' will be recovered",
        };
        try {
            await this.dialogService.show(params);
            const result = await firstValueFrom(this.#clientsApiService.restoreClient(customerId as Uuid));
            const recovered = !result.errors?.length;
            if (recovered) {
                this.logger.success('Client has been recovered');
                this.storesService.refreshCustomers();
            } else {
                this.logger.error('Error occurred trying to recover the client.');
            }
            return recovered;
        } catch (_) {
            return false;
        }
    }

    // @ts-ignore TS7006
    viewClient(client) {
        const promise = new Promise((resolve, reject) => {
            const initialState = { client };
            const modalRef = this.helperService.showModalAside(ClientDetailsViewComponent, { initialState });
            // @ts-ignore TS2532
            modalRef.content.action.pipe(take(1)).subscribe((result: any) => {
                if (!result.cancelled) {
                    this._saveContact(result.client);
                    resolve(true);
                } else {
                    this._cancelContact(client);
                    reject();
                }
            });
        });

        return promise;
    }

    // @ts-ignore TS7006
    _cancelContact(client) {
        client.entityAspect.rejectChanges();
    }

    // @ts-ignore TS7006
    _saveContact(client) {
        this.repoCustomers.Save(client, 'Contact Saved');
    }
}
